// no itx for this file
import Channel, { ChannelDescriptionProperty, tagForChannelType as tagFromChannelType } from "../models/Channel";
import { ChannelType, channelTypeFromString, channelTypeInfo } from "../models/ChannelType";
import type Signal from "../models/Signal";
import ButtonChannel from "../models/ButtonChannel";
import { sortArrayByIntProperty } from "../models/utils";
import Logger from "../Logger";
import type Device from "../models/Device";
import SignalTypes, { getSignalTypeByName } from "../models/SignalTypes";
import determineActorComObjectFunction from "./determineActorComObjectFunction";
import { ResultObjectButtonSide, MapSignalToTagResult, RockerConfig } from "../models/KnxRules";
import type Button from "../models/Button";
import { noItx } from "../translationItx/itx";
import DebugOutput from "../DebugOutput";
import { isUiModeProductAdmin } from "../uiMode";
import { nameToShortName } from "../models/utils";
import { TAG_IGNORE, TAG_SPECIAL, TAG_TEMPERATUR } from "../models/Constants";
import { belongsToBundle } from "../models/SignalBundles";
import { merge_Blind_Channels_for_Buttons, merge_TX_S_Channels } from "./mergeChannels";
import determineButtonComObjectFunction from "./determineButtonComObjectFunction";

import translateChannelName from "./scriptTrsnslation";
import { isDevelopmentEnvironment } from "../estada-config-release";

const logger = new Logger();

function strippedName(name: string): string {
  return nameToShortName(name);
}

/**
 * Merges channels rawSceneData not containing any signals with the last preceeding channel containing signals matching the same channel name.
 * Initial rawSceneData found prior to any signal will be merged with the first channel containing signals.
 * @param channels
 * @returns resulting channels
 */

function buttonNumberToTag(
  button: Device,
  buttonNumber: string | number,
  buttonSide: ResultObjectButtonSide | undefined,
): string {
  //
  let tag = "T??";

  if (button.baseProps.buttonBaseProps?.supportsSplitRocker) {
    //
    // dieser button unterstützt RockerConfig
    //
    // dh aus allen Tasten wird T02, T02L, T02R etc
    //

    if (typeof buttonNumber == "string") {
      const b = parseInt(buttonNumber);
      if (buttonNumber === String(b)) {
        buttonNumber = b;
      }
    }

    if (typeof buttonNumber == "number") {
      const buttonNumber2 = Math.floor((buttonNumber - 1) / 2) + 1;
      tag = "T" + buttonNumber2.toString().padStart(2, "0");
      if (buttonSide != "both") {
        if (buttonNumber % 2 == 1) {
          tag += "L";
        } else {
          tag += "R";
        }
      }
    } else {
      tag = "T" + buttonNumber;
    }
  } else {
    //
    // dieser button unterstützt KEINE RockerConfig
    //
    if (typeof buttonNumber == "number") {
      tag = "T" + buttonNumber.toString().padStart(2, "0");
    } else {
      tag = "T" + buttonNumber;
    }
  }

  return tag;
}

export function getRequestChannelforMapSignalToTagResult(
  button: Button,
  resultObject: MapSignalToTagResult,
): ButtonChannel | undefined {
  const buttonNumber = resultObject.buttonNumber;

  if (buttonNumber && typeof buttonNumber == "number") {
    let tag = resultObject.tag || buttonNumberToTag(button, buttonNumber, resultObject.buttonSide);
    const res = button.buttonChannels.getItemByTag(tag);
    return res;
  }
}

export function createChannelsByRule(device: Device, isActor: boolean, newSignals: Array<Signal>): Array<Channel> {
  ////////////////////////////////////////////////////
  //
  // collect all signals of all channel in one array
  //
  ////////////////////////////////////////////////////

  // const device = channels.at(0)?.parent as Device;
  // var allSignals = new Array<Signal>();
  // channels.forEach((channel) => {
  //   channel.signals.forEach((signal) => allSignals.push(signal));
  // });

  const allSignals = sortArrayByIntProperty(newSignals, "number");

  // console.log("allSignals.length a", allSignals.length);
  // allSignals.forEach((signal) => {
  //   console.log(signal.number, signal.name, signal.type.name);
  // });

  let unmatchedSignals = new Array<Signal>();
  let newChannels = new Array<Channel>();

  allSignals.forEach(async (signal) => {
    let tag = "";

    let result = device.template?.knxProductData.currentRules?.mapSignalToTag(device, signal);
    if (result) {
      const resultObject = result as MapSignalToTagResult;

      let channelType = ChannelType.Unknown;
      let channelName: string | undefined;
      let isVirtualRocker = false;

      if (typeof result == "object" && result.ignore) {
        resultObject.signalType = SignalTypes.Ignored.name;
      }

      if (resultObject.channelType && !resultObject.tag && !resultObject.channelNumber) {
        //
        // setzte automatisch den tag für spezielle ChannelTypes
        //
        const t = tagFromChannelType(channelTypeFromString(resultObject.channelType));
        if (t) {
          tag = resultObject.tag = t;
        }
      }

      const hasXxxNumber =
        typeof result == "object" &&
        (result.buttonNumber || // ButtonChannel
          result.channelNumber || // ActorChannel
          result.statusNumber || // StatusChannel
          result.logicNumber ||
          result.specialNumber); // LogicChannel

      //
      // allow complex return value from mapSignalToTag
      //
      if (typeof result == "object" && result.ignore && !hasXxxNumber) {
        tag = TAG_IGNORE;
        channelName = "Unbenutzte Signale";
        channelType = ChannelType.Ignored;
      } else if (hasXxxNumber) {
        tag = resultObject.tag || "";
        channelName = resultObject.channelName;
        if (channelName && channelName.toLowerCase().indexOf("dummy") >= 0) {
          channelName = "";
        }

        ////////////////////////////////////
        //
        // Property: ChannelType
        //
        ////////////////////////////////////

        if (resultObject.channelType) {
          if (resultObject.channelType == "fromRequest") {
            const channelType1 = device.channels.getItemByTag(tag);
            if (channelType1) {
              channelType = channelType1.channelType;
            }
          } else {
            channelType = channelTypeFromString(resultObject.channelType);
          }
        }

        ////////////////////////////////////
        //
        // Property: signalType -> is not for channel is for signal
        //
        ////////////////////////////////////

        if (resultObject.signalType) {
          signal.oldType = signal.type;
          signal.type = getSignalTypeByName(resultObject.signalType);
          signal.determinedFromId = "manualViaScript";
        }

        ////////////////////////////////////
        //
        // Status
        //
        ////////////////////////////////////

        if (resultObject.statusNumber != undefined) {
          channelName ||= resultObject.channelName ||= "Status " + (resultObject.statusNumber || "??");

          if (typeof resultObject.statusNumber == "number") {
            tag ||= "S" + String(resultObject.statusNumber).padStart(2, "0");
          } else {
            tag ||= "S" + resultObject.statusNumber;
          }
        }

        ////////////////////////////////////
        //
        // ButtonChannel
        //
        ////////////////////////////////////
        else if (resultObject.buttonNumber != undefined) {
          let buttonNumber: any = resultObject.buttonNumber;

          if (buttonNumber == "slap") {
            tag = "TP";
            channelName ||= "Patsch";
          } else if (buttonNumber == "temperature") {
            tag = TAG_TEMPERATUR;
            channelName ||= "Temperatur-Messung";
          } else {
            if (typeof buttonNumber == "string") {
              const b = parseInt(buttonNumber);
              if (!isNaN(b) && buttonNumber.match(new RegExp("^[0-9]+$"))) {
                buttonNumber = b;
              }
            }

            tag = resultObject.tag || buttonNumberToTag(device, buttonNumber, resultObject.buttonSide);
            channelName = resultObject.channelName || nameToShortName(signal.name);
            if (channelName && channelName.toLowerCase().indexOf("dummy") >= 0) {
              channelName = "";
            }

            if (typeof buttonNumber != "string" && device.baseProps.buttonBaseProps?.supportsSplitRocker) {
              //
              // Nur für ButtonChannel in Buttons die RockerConfig unterstützen
              //

              const buttonNumber2 = Math.floor((buttonNumber - 1) / 2) + 1;

              const requestChannel = getRequestChannelforMapSignalToTagResult(device as Button, resultObject);
              let rockerConfig = requestChannel?.rockerConfig;

              //
              // FALLS das Geräte/Button Rocker unterstütz,
              // UND bisher noch keine für diesen ButtonChannel gesetzt wurde
              //
              // DANN leiten wir die RockerConfig von der MapSignalToTagResult ab
              //
              // Das passiert nur einmal,
              // danach verhalten wir uns als ob der Benutzer eben diese Auswahl getroffen hätte
              //
              // Das passiert insbesondere bei ersten Öffnen des Button
              // nach dem Zuweisen eines Produkts
              //
              //
              if (
                requestChannel &&
                rockerConfig == RockerConfig.none &&
                device.baseProps.buttonBaseProps?.supportsSplitRocker
              ) {
                if (resultObject.buttonSide) {
                  if (resultObject.buttonSide == "both") {
                    rockerConfig = requestChannel.rockerConfig = RockerConfig.rocker;
                  } else {
                    rockerConfig = requestChannel.rockerConfig = RockerConfig.single;
                  }
                }
              }

              //
              // Kosistenz test für RockerConfig wie vorgegeben und wie in MapSignalToTag ausgerechent
              //
              if (isUiModeProductAdmin()) {
                if (requestChannel && device.baseProps.buttonBaseProps?.supportsSplitRocker) {
                  if (resultObject.buttonSide) {
                    let err = false;
                    if (resultObject.buttonSide == "both") {
                      if (requestChannel.rockerConfig != RockerConfig.rocker) {
                        err = true;
                      }
                    } else {
                      if (requestChannel.rockerConfig != RockerConfig.single) {
                        err = true;
                      }
                    }

                    if (err) {
                      const s = noItx(
                        `RockerConfig for ButtonChannel ${requestChannel.tag} is not compatible with MapSignalToTagResult: ${requestChannel.rockerConfig}/${resultObject.buttonSide}`,
                      );
                      logger.error(s);
                      DebugOutput.outputError("", s);
                    }
                  }
                }
              }

              switch (rockerConfig) {
                case RockerConfig.none:
                  break;

                case RockerConfig.single:
                  channelName ||= "Einzeltaste " + buttonNumber;
                  break;

                case RockerConfig.rocker:
                  if (!resultObject.buttonSide) {
                    //
                    // MWE 2.6.2025
                    //
                    // Wenn wir mit einer rechten (und somit expliziten) Taste hier vorbei kommen,
                    // dann hat das Geräte offen sichtlich den Rocker nicht selbst als zb. "Buttons 1/2
                    // erzeugt.
                    //
                    // Somit ist es eine virtueller Rocker, der nur von Estada verwaltet wird
                    //
                    isVirtualRocker = true;
                  }
                  break;

                default:
                  //
                  // insbesondere rockerConfig == undefined
                  //
                  if (resultObject.buttonSide == "l" || resultObject.buttonSide == "r") {
                    tag += resultObject.buttonSide.toLocaleUpperCase();
                  }
              }
            }

            const useChannelTypeSensitiveSignalMatching = false;
            if (useChannelTypeSensitiveSignalMatching) {
              const requestChannel = device.channels.getItemByTag(tag);
              if (requestChannel) {
                determineButtonComObjectFunction(signal, requestChannel.channelType);
              }
            }
          }

          if (resultObject.extraLongPress) {
            const t = resultObject.buttonNumber;
            tag += "_x";
          } else if (resultObject.longPress) {
            const t = resultObject.buttonNumber;
            tag += "_l";
          }
        }

        ////////////////////////////////////
        //
        // ActorChannel
        //
        ////////////////////////////////////
        else if (resultObject.channelNumber) {
          if (device.isActor) {
            if (typeof resultObject.channelNumber == "number") {
              tag ||= String(resultObject.channelNumber).padStart(2, "0");
            } else {
              tag ||= resultObject.channelNumber;
            }
          } else {
            channelName ||= resultObject.channelName ||= "Aktor-Kanal " + (resultObject.channelNumber || "??");

            if (typeof resultObject.channelNumber == "number") {
              tag ||= "A" + String(resultObject.channelNumber).padStart(2, "0");
            } else {
              tag ||= "A" + resultObject.channelNumber;
            }
            if (signal.determinedFromId != "manual" && signal.determinedFromId != "manualViaScript") {
              determineActorComObjectFunction(signal);
            }
          }
        }

        ////////////////////////////////////
        //
        // Special
        //
        ////////////////////////////////////

        if (result.specialNumber) {
          channelName ||= resultObject.channelName ||= "Spezial Kanal " + (resultObject.specialNumber || "??");

          if (typeof resultObject.specialNumber == "number") {
            tag ||= "TX_" + String(resultObject.specialNumber).padStart(2, "0");
          } else {
            tag ||= "TX_" + resultObject.specialNumber;
          }
        }
        ////////////////////////////////////
        //
        // Logic
        //
        ////////////////////////////////////
        if (result.logicNumber) {
          //
          // Wir betrachten eine Logik-Gruppe in 2 Teilen:
          //
          // 1: Eingang: wie einen Aktor-Kanal (Eingang)
          // 2: Ausgang: wie einen Button (Ausgang)
          //

          //
          // if logicInput and logicOutut are set, Input wins
          //

          let isInput = true;
          if (result.logicInput) {
          } else if (result.logicOutput) {
            isInput = false;
          }

          let number0 = "";
          if (Array.isArray(result.logicNumber)) {
            result.logicNumber = result.logicNumber.join("");
          } else {
            number0 = `${result.logicNumber}`;
          }

          channelName = resultObject.channelName;
          if (!channelName) {
            if (isInput) {
              channelName = `Input: ${signal.name}`;
            } else {
              channelName = `Output: ${signal.name}`;
            }
          }

          if (isInput) {
            tag ||= `AL${resultObject.logicNumber}.${signal.number}`;
            determineActorComObjectFunction(signal);
          } else {
            tag ||= `TL${resultObject.logicNumber}.${signal.number}`;
          }
        }
      } else if (typeof result == "string") {
        tag = (result as string) || "??";
      }

      if (tag) {
        let newChannel = newChannels.find((c) => c.tag == tag);
        //
        // MWE 21.04.2026
        //
        // Handling für DummySignale als erstes Signal eines Channels
        //
        const signalChannelName = signal.isDummySignal ? "" : signal.channelName;

        if (newChannel) {
          if (!newChannel.name && !signal.isDummySignal) {
            newChannel.name = translateChannelName(channelName || signalChannelName || "");
          }
        } else {
          if (isActor) {
            newChannel = new Channel({ isRequestChannel: false, isCreatedFromScript: true }, device);
          } else {
            const newButtonChannel = (newChannel = device.createNewKnxChannel({ isCreatedFromScript: true }, device));
            newButtonChannel.isVirtualRocker = isVirtualRocker;
            if (newButtonChannel.isButtonChannel) {
              (newButtonChannel as ButtonChannel).knxButtonSceneNumberProperty = signal.knxSceneNumberProperty;
            }
          }

          newChannel.tag = tag;

          newChannel.name = translateChannelName(channelName || signalChannelName || "");

          newChannel.channelType = tag != TAG_SPECIAL ? channelType : ChannelType.Special;
          newChannels.push(newChannel);

          // const oldChannel = findOldChannelBySignalNumber(channels, signal.number);
          // if (oldChannel) {
          //   newChannel.mergeKnxChannelPropertyX(oldChannel);
          //   newChannel.mergeKnxChannelPropertyX(signal);
          // }
        }
        newChannel.mergeKnxChannelPropertyX(signal);

        signal.parent = newChannel;
        newChannel?.signals.addWithoutUpdate(signal);
      } else {
        unmatchedSignals.push(signal);
      }
    } else {
      unmatchedSignals.push(signal);
    }
  });

  /////////////////////////////////////////////////////////////////////////////////////////
  //
  // Move unmatched signals belonging to the EnvironmentChannel
  // before appling createChannelsByComObjectNames
  //
  const environmentSignals = unmatchedSignals.filter(
    (channelSignal) =>
      channelSignal.type.name != SignalTypes.Scene.name && belongsToBundle(ChannelType.Environment, channelSignal),
  );

  if (environmentSignals.length > 0) {
    const environmentChannel = device.createEnvironmentChannel();
    newChannels.push(environmentChannel);
    for (const s of environmentSignals) {
      environmentChannel.addSignalWithoutUpdate(s);
    }
    unmatchedSignals = unmatchedSignals.filter((s) => !environmentSignals.includes(s));
  }
  //
  /////////////////////////////////////////////////////////////////////////////////////////

  const newChannels2 = createChannelsByComObjectNames(device, unmatchedSignals);

  newChannels = newChannels.concat(newChannels2);
  newChannels = merge_TX_S_Channels(newChannels);
  if (device.isButton) {
    newChannels = merge_Blind_Channels_for_Buttons(newChannels);
  }

  const xxSignals = new Array<Signal>();
  newChannels.forEach((channel) => {
    channel.signals.forEach((signal) => {
      const duplicateSignal = xxSignals.find((s) => s.number == signal.number);
      if (duplicateSignal) {
        logger.error(`Signal ${signal.number} is duplicate`);
      } else {
        xxSignals.push(signal);
      }
    });
  });

  // console.log("unmatchedSignals.length a", unmatchedSignals.length);
  // allSignals.forEach((signal) => {
  //   console.log(signal.number, signal.name, signal.type.name);
  // });

  return newChannels;
}

export function createChannelsByComObjectNames(device: Device, allSignals: Array<Signal>): Array<Channel> {
  const names = new Set<string>();

  allSignals.forEach((signal) => {
    if (signal.type.name == SignalTypes.Unknown.name || signal.type.name == SignalTypes.Ignored.name) {
      return;
    }

    var name = strippedName(signal.channelName);
    names.add(name);
  });

  allSignals = sortArrayByIntProperty(allSignals, "number");

  //
  // create new channels  signal.name => channel.name
  //
  const newChannels = new Array<Channel>();
  names.forEach((name) => {
    var newChannel: Channel;
    newChannel = device.createNewKnxChannel({}, device);
    newChannels.push(newChannel);

    const duplicateChannel = newChannels.find((c) => c.name == name);
    if (duplicateChannel) {
      duplicateChannel.name = duplicateChannel.signals.asArray()[0]?.name || "???";
    }

    newChannel.name = name;
    newChannel.knxChannelId = name;

    //
    // search all signals belonging to this channel
    //

    let lastChannelDescriptionProperty: ChannelDescriptionProperty | undefined = undefined;

    for (let i = 0; i < allSignals.length; i++) {
      let signal = allSignals[i];
      //
      // match signals to channels by name mathing
      //
      // BUT !!!!
      // as long as the "channelDescription" is the same,
      // we can asume "we are still in the same channel"
      //
      // "channelDescription" wins over pure name mathing
      //

      let sameChannel =
        signal.channelName == name ||
        strippedName(signal.channelName) == name ||
        (lastChannelDescriptionProperty && lastChannelDescriptionProperty == signal.knxChannelDescriptionProperty);

      if (sameChannel && newChannel.name == "") {
        newChannel.name = name;
        console.log("MWE-CH-x", name);
      }

      if (
        sameChannel &&
        signal.type.name != SignalTypes.Unknown.name &&
        signal.type.name != SignalTypes.Ignored.name &&
        !signal.type.globalGroupAdress &&
        newChannel.getSignalByType(signal.type)
      ) {
        //
        // Channel has already a signal of this type.
        // 2 Signals of the same type are not possible in the same channel,
        // so we open a new one

        newChannel = device.createNewKnxChannel({}, device);
        newChannels.push(newChannel);

        const duplicateChannel = newChannels.find((c) => strippedName(c.name) == name);
        if (duplicateChannel) {
          duplicateChannel.name = duplicateChannel.signals.asArray()[0]?.channelName || "???";
          name = signal.channelName;
        }

        newChannel.name = name;
        newChannel.knxChannelId = signal.channelName;
      }

      if (sameChannel) {
        // const oldChannel = findOldChannelBySignalNumber(channels, signal.number);

        // if (oldChannel) {
        //   newChannel.mergeKnxChannelPropertyX(oldChannel);
        //   newChannel.mergeKnxChannelPropertyX(signal);
        // }

        newChannel.mergeKnxChannelPropertyX(signal);

        signal.parent = newChannel;
        newChannel.signals.addWithoutUpdate(signal);

        //
        // remove signal form input array after it is moved into a channel
        //
        allSignals.splice(i, 1);
        i--;
      }
    }

    if (newChannel.signals.size == 1 && newChannel.signals.asArray()[0].name.indexOf("DUMMY") >= 0) {
      newChannel.channelType = ChannelType.UnknownMapWithScript;
    }
  });

  if (allSignals.length > 0) {
    let newChannel = newChannels.find((ch) => ch.channelType == ChannelType.Special || ch.tag == TAG_SPECIAL);
    if (!newChannel) {
      newChannel = device.createNewKnxChannel(
        { tag: TAG_SPECIAL, name: "Spezial Signale", isRequestChannel: false },
        device,
      );
    }
    newChannel.channelType = ChannelType.Special;

    allSignals.forEach((signal) => {
      signal.parent = newChannel;
      newChannel.signals.addWithoutUpdate(signal);
    });
  }

  const specialChannels = newChannels.filter((ch) => ch.channelType == ChannelType.Special);
  console.log("specialChannels", specialChannels);

  //
  // make channel names unique
  //
  for (let i = 0; i < newChannels.length - 1; i++) {
    let uniqueIndex = 1;
    for (let j = i + 1; j < newChannels.length; j++) {
      if (newChannels[i].name === newChannels[j].name) {
        newChannels[j].name += `-${uniqueIndex++}`;
      }
    }
  }

  if (device.isDecentralActor) {
    newChannels.forEach((channel) => {
      channel.tag = "A" + channel.tag;
    });
  }

  return newChannels;
}
